import type {
  InventoryValueEntry as ApiInventoryValueEntry,
  Manufacturer as ApiManufacturer,
  SalesData as ApiSalesData,
} from "randmar-api-client";

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default class Manufacturer {
  onHold?: number;
  aREmail?: string;
  address1?: string;
  address2?: string;
  autoReorderDate?: number;
  autoUpdate?: boolean;
  city?: string;
  contactName?: string;
  createdDate?: number;
  creditLimit?: number;
  claimEmail?: string;
  currencyCode?: string;
  defaultLeadTime?: string;
  defaultMaxInventoryValue?: number;
  defaultMinimumOrder?: number;
  defaultWeeksOfStocking?: string;
  domain?: string;
  googleEmail?: string;
  manufacturerId?: string;
  name?: string;
  outstandingBalance?: number;
  partnerPortal?: string;
  pOEmail?: string;
  phone?: string;
  postalCode?: string;
  province?: string;
  publicName?: string;
  representativeEmail?: string;
  shopifyWebsite?: string;
  terms?: string
  website?: string;
  publicEmail?: string;
  defaultTaxGroup?: string;
  salesData?: SalesData;
  fiscalYearMonthStart?: number;

  public IsOnHold(): boolean {
    return this.onHold === 1;
  }

  public AvailableCredit(): number {
    return (this.creditLimit || 0) - (this.outstandingBalance || 0);
  }

  get reorderDay() {
    if (this.autoReorderDate !== undefined && this.autoReorderDate >= 0 && this.autoReorderDate < DAYS_OF_WEEK.length) {
      return DAYS_OF_WEEK[this.autoReorderDate];
    }
    return 'ASAP';
  }

  public static convertFrom(source: ApiManufacturer): Manufacturer {
    const manufacturer = new Manufacturer();

    manufacturer.onHold = source.OnHold;
    manufacturer.aREmail = undefined;
    manufacturer.address1 = source.Address1 ?? undefined;
    manufacturer.address2 = source.Address2 ?? undefined;
    manufacturer.autoReorderDate = source.AutoReorderDate;
    manufacturer.autoUpdate = source.AutoUpdate;
    manufacturer.city = source.City ?? undefined;
    manufacturer.contactName = source.ContactName ?? undefined;
    manufacturer.createdDate = source.CreationDate;
    manufacturer.creditLimit = source.CreditLimit;
    const legacyClaimEmail = (source as ApiManufacturer & { ClaimEmail?: string | null }).ClaimEmail;
    manufacturer.claimEmail = legacyClaimEmail ?? undefined;
    manufacturer.currencyCode = source.CurrencyCode ?? undefined;
    manufacturer.defaultLeadTime =
      source.DefaultLeadTime !== undefined && source.DefaultLeadTime !== null ? String(source.DefaultLeadTime) : undefined;
    manufacturer.defaultMaxInventoryValue = source.DefaultMaxInventoryValue ?? undefined;
    manufacturer.defaultMinimumOrder = source.DefaultMinimumOrder ?? undefined;
    manufacturer.defaultWeeksOfStocking =
      source.DefaultWeeksOfStocking !== undefined && source.DefaultWeeksOfStocking !== null
        ? String(source.DefaultWeeksOfStocking)
        : undefined;
    manufacturer.domain = source.Domain ?? undefined;
    manufacturer.googleEmail = undefined;
    manufacturer.manufacturerId = source.ManufacturerId ?? undefined;
    manufacturer.name = source.Name ?? undefined;
    manufacturer.outstandingBalance = source.OutstandingBalance ?? undefined;
    manufacturer.partnerPortal = source.PartnerPortal ?? undefined;
    manufacturer.pOEmail = source.POEmail ?? undefined;
    manufacturer.phone = source.Phone ?? undefined;
    manufacturer.postalCode = source.PostalCode ?? undefined;
    manufacturer.province = source.Province ?? undefined;
    manufacturer.publicName = source.PublicName ?? undefined;
    manufacturer.representativeEmail = source.RepresentativeEmail ?? undefined;
    manufacturer.shopifyWebsite = undefined;
    manufacturer.terms = source.Terms ?? undefined;
    manufacturer.website = source.Website ?? undefined;
    manufacturer.publicEmail = source.PublicEmail ?? undefined;
    manufacturer.defaultTaxGroup = undefined;
    manufacturer.fiscalYearMonthStart =
      typeof source.FiscalYearMonthStart === "number" ? source.FiscalYearMonthStart - 1 : undefined;

    if (source.SalesData) manufacturer.salesData = SalesData.convertFrom(source.SalesData);

    return manufacturer;
  }
}

export class InventoryValue {
  inventoryValue?: number;
  purchaseOrderValue?: number;
  salesOrderValue?: number;
  location?: string;
  category?: string;

  public static convertFrom(source: ApiInventoryValueEntry): InventoryValue {
    const inventoryValue = new InventoryValue();

    inventoryValue.inventoryValue = source.InventoryValue;
    inventoryValue.purchaseOrderValue = source.PurchaseOrderValue;
    inventoryValue.salesOrderValue = source.SalesOrderValue;
    inventoryValue.location = source.Location ?? undefined;
    inventoryValue.category = source.Category ?? undefined;

    return inventoryValue;
  }
}

export class SalesData {
  salesLast3Months?: number;
  salesLast30Days?: number;
  salesYearToDay?: number;

  public static convertFrom(source: ApiSalesData): SalesData {
    const salesData = new SalesData();

    salesData.salesLast3Months = source.SalesLast3Months;
    salesData.salesLast30Days = source.SalesLast30Days;
    salesData.salesYearToDay = source.SalesYearToDay;

    return salesData;
  }
}
